import {
    ApiHandlerDiscreteParameter,
    HasOnError,
    addToEndOfChain,
    addToStartOfChain,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import {
    chain,
    PopulateApiParameter,
    SendTracking,
} from "@NA/Overrides/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrder";
import formatDateWithTimezone from "@insite/client-framework/Common/Utilities/formatDateWithTimezone";
import { Cart, CartResult, UpdateCartApiParameter } from "@insite/client-framework/Services/CartService";
import {
    addCartModifiedToLocalStorage,
    getIfCartIsModifiied,
    removeCartModifiedFromLocalStorage,
} from "@NA/Utils/Utility";
import { trackPurchaseForIterable, updateCartForIterable } from "@NA/Services/ADIAbandonedCartV2Service";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import {
    pushCheckoutOptionDataToDataLayer,
    pushOrderConfirmationDataToDataLayer,
} from "@NA/Overrides/Common/Utilities/CustomTracking";
import { CartLineModel, ShipViaDto } from "@insite/client-framework/Types/ApiModels";

export interface PlaceOrderParameter {
    paymentMethod: string;
    poNumber: string;
    reference?: string;
    requestor?: string;
    vatNumber?: string;
    saveCard: boolean;
    cardHolderName: string;
    cardNumber: string;
    cardType: string;
    expirationMonth: number;
    expirationYear: number;
    securityCode: string;
    useBillingAddress: boolean;
    address1: string;
    countryId: string;
    stateId: string;
    city: string;
    postalCode: string;
    onSuccess?: (cartId: string) => void;
    payPalToken?: string;
    payPalPayerId?: string;
    accountHolderName?: string;
    accountNumber?: string;
    routingNumber?: string;
    properties?: any;
    selectedCCId?: any;
    shipVia?: ShipViaDto;
}

export interface CartCustom extends Cart {
    finalGrandTotal: number;
    finalGrandTotalDisplay: string;
}

export type CustomParameter = HasOnError & PlaceOrderParameter;

const convertDateToApiFormat = (date: Date | null) => (date ? formatDateWithTimezone(date) : "");

type HandlerType = ApiHandlerDiscreteParameter<
    CustomParameter,
    UpdateCartApiParameter,
    CartResult,
    {
        cartToUpdate: Cart;
    }
>;

export const PopulateApiParameterCustom: HandlerType = props => {
    if (!props.cartToUpdate) {
        return;
    }
    const currenySymbol = props.cartToUpdate.currencySymbol;

    const {
        pages: {
            checkoutReviewAndSubmit: { requestedDeliveryDate, requestedPickupDate },
        },
    } = props.getState();

    const state = props.getState() as CustomApplicationState;
    var alternateShipId = state.ADIGlobalData.checkoutCartStore.alternateShipTo?.id;
    const shippingSelection = state.ADIGlobalData.checkoutCartStore.cart?.properties.shippingSelection;
    const pickupDate = state.ADIGlobalData.shippingMethodStore.initialPickUpDate;
    const defaultShipId = state.context.session.shipToId;
    const tempDeliveryDate =
        typeof requestedDeliveryDate !== "undefined"
            ? requestedDeliveryDate
            : props.cartToUpdate.requestedDeliveryDateDisplay;
    const tempPickupDate =
        typeof requestedPickupDate !== "undefined"
            ? requestedPickupDate
            : props.cartToUpdate.requestedPickupDateDisplay;

    if (alternateShipId !== props.cartToUpdate.shipToId) {
        alternateShipId = props.cartToUpdate.shipToId;
    }

    let shipVia = null;
    let carrier = null;
    const ship = props?.cartToUpdate?.carriers?.filter(c => c.description == "Ship")[0];
    const pickUp = props?.cartToUpdate?.carriers?.filter(c => c.description == "Pick-Up in Branch")[0];
    if ((shippingSelection === "primary" || shippingSelection === "alternate") && ship && ship.shipVias && ship.shipVias.length > 0) {
        carrier = ship;
        shipVia = ship?.shipVias[0];
    } else if (shippingSelection === "pickup" && pickUp && pickUp.shipVias && pickUp.shipVias.length > 0) {
        carrier = pickUp;
        shipVia = pickUp?.shipVias[0];
    }

    var shipPartial = "";
    const cartLines = props.cartToUpdate.cartLines;
    if ((cartLines?.length === 1 && cartLines[0].qtyOrdered == 1) || shippingSelection === "pickup") {
        shipPartial = "false";
    } else if (props.cartToUpdate.properties["shipPartial"] !== "") {
        shipPartial = props.cartToUpdate.properties["shipPartial"];
    } else {
        shipPartial = props.cartToUpdate?.shipPartial && props.cartToUpdate?.shipPartial !== true ? "false" : "true";
    }

    const willCallRush = state.ADIGlobalData.checkoutCartStore.cart?.properties?.willCallRush ?? "";
    const adiLockInd = state.ADIGlobalData.checkoutCartStore.cart?.properties?.adiLockInd ?? "";
    
   const isTBD = state.ADIGlobalData.shippingMethodStore.shippingMethods?.deliveryMethod[0]?.isTBD;
    

    props.apiParameter = {
        cart: {
            ...props.cartToUpdate,
            properties: {
                ...props.cartToUpdate.properties,
                poNumber: props.cartToUpdate.poNumber,
                pickUpDate: pickupDate,
                reference: props.parameter.properties.reference,
                requestor: props.parameter.properties.requestor,
                job: props.parameter.properties.job,
                shipPartial: shipPartial,
                // finalGrandTotal: props.cartToUpdate.finalGrandTotal?.toFixed(2), // Code from Spire Prod
                // finalGrandTotal: isTBD ?  props.cartToUpdate.orderGrandTotal : parseFloat(props.cartToUpdate.properties.finalGrandTotal)?.toFixed(2), // This change is being used by P21: 
                finalGrandTotal: !props.parameter.properties?.isPickUp && isTBD ?  props.cartToUpdate.orderGrandTotal : parseFloat(props.cartToUpdate.properties.finalGrandTotal)?.toFixed(2), 
                totalTax: props.parameter.properties.totalTax, // This change is being used by P21
                // totalTax: props.cartToUpdate.totalTax?.toFixed(2), // Code from Prod SPire
                account: "",
                branch: "", // Change by P21
                // branch: props.parameter.properties.branch, // Code from Spire Prod
                nationalAccount: "",
                selectedCCId: props.parameter?.selectedCCId ? props.parameter?.selectedCCId : "",
                town: "",
                isLTL: props.parameter.properties?.isLTL
                    ? props.parameter.properties?.isLTL
                    : props.cartToUpdate.properties.isLTL,
                LTLLiftGateRequired: props.parameter.properties.LTLLiftGateRequired,
                LTLInSideDelivery: props.parameter.properties.LTLInSideDelivery,
                isPickUpAdiLocker: props.parameter.properties?.isPickUpAdiLocker
                    ? props.parameter.properties?.isPickUpAdiLocker
                    : props.cartToUpdate.properties.isPickUpAdiLocker,
                willCallRush,
                adiLockInd,
                isPickUp: props.parameter.properties?.isPickUp
                    ? props.parameter.properties?.isPickUp
                    : props.cartToUpdate.properties.isPickUp,
                totalOrderAmount: props.cartToUpdate.orderGrandTotal.toFixed(2),
                swapPricingForSubmit: "true",
                swapShippingTypeForSubmit: "true",
            },
            shipVia: shipVia,
            shipToId: shippingSelection === "alternate" ? alternateShipId : defaultShipId,
            carrier: carrier,
            requestedDeliveryDate: convertDateToApiFormat(tempDeliveryDate),
            requestedPickupDate: convertDateToApiFormat(tempPickupDate),
            shippingAndHandling: Number(props.cartToUpdate.properties["freightCharge"]),
            shippingAndHandlingDisplay: `${currenySymbol}${props.cartToUpdate.properties["freightCharge"]}`,
            totalTax: Number(props.cartToUpdate.properties["totalTax"]),
            totalTaxDisplay: `${currenySymbol}${props.cartToUpdate.properties["totalTax"]}`,
            finalGrandTotal: Number(props.cartToUpdate.properties["finalGrandTotal"]),
            finalGrandTotalDisplay: `${currenySymbol}${props.cartToUpdate.properties["finalGrandTotal"]}`,
            fulfillmentMethod:
                props.cartToUpdate.properties["isPickUp"] === "Y" ? "Pickup" : props.cartToUpdate.fulfillmentMethod,
        } as CartCustom,
    };
};

replaceInChain(chain, PopulateApiParameter, PopulateApiParameterCustom);

export const CallPurchaseCompleteIterableApi: HandlerType = props => {

    const isIterableEnabled = (props.getState().context.settings as any).settingsCollection?.adiIterableSettingSettings
        ?.isIterableEnabled;
    if (isIterableEnabled) {
        trackPurchaseForIterable(props.apiResult.cart);
        removeCartModifiedFromLocalStorage();
    }
};

addToEndOfChain(chain, CallPurchaseCompleteIterableApi);

export const PushOrderConfirmationDataToDataLayer: HandlerType = props => {
    const cartLines: CartLineModel[] = props.cartToUpdate.cartLines ?? [];
    pushOrderConfirmationDataToDataLayer(props.apiResult.cart, cartLines, "", props.getState().context.session);
};

addToEndOfChain(chain, PushOrderConfirmationDataToDataLayer);

export const RemovetrackCompletedOrder: HandlerType = props => {};

replaceInChain(chain, SendTracking, RemovetrackCompletedOrder);
